import type {
  LoginPlayer,
  PlayerReteamPlayer,
  ScoreBoardData,
  ServerChatPublicMessage,
  ServerChatSayMessage,
  ServerChatTeamMessage,
  ServerChatVoteMutePassedMessage,
  ServerChatWhisperMessage,
  ServerEventBoostMessage,
  ServerEventBounceMessage,
  ServerEventLeaveHorizonMessage,
  ServerEventStealthMessage,
  ServerPlayerFireMessage,
  ServerPlayerFlagMessage,
  ServerPlayerHitMessage,
  ServerPlayerKillMessage,
  ServerPlayerLevelMessage,
  ServerPlayerNewMessage,
  ServerPlayerPowerupMessage,
  ServerPlayerRespawnMessage,
  ServerPlayerTypeMessage,
} from '@airmash/protocol';
import { GAME_TYPES, PLAYER_STATUS } from '@airmash/protocol';
import type { FlagCode } from '../generated/flags';
import { UNKNOWN_FLAG } from '../services/Flags';
import { uiState, type ChatMessageType, type UIPlayer } from '../ui/UIState';
import { answerPing, pongDisplayText } from './ClientPing';
import { clock } from './Clock';
import { pushGameLogJoin, pushGameLogKill, pushGameLogLeave } from './GameLog';
import { cancelSpectatorSwitchOnRespawn, spectatorSwitch as gamesSpectatorSwitch } from './Games';
import { setCamera as graphicsSetCamera } from './Graphics';
import { timing as networkTiming } from './Network';
import type { BadgeTextureName } from './Player';
import Player, { PLAYER_TIMEOUT_MS } from './Player';
import { GAME_STATE_CODES, session } from './Session';
import { setSettings } from './Settings';
import { clamp as toolsClamp } from './Tools';
import { UIClick as soundUIClick } from './Sound';
import {
  addChatLine as uiAddChatLine,
  addSayLine as uiAddSayLine,
  chatVotemutePass as uiChatVotemutePass,
  isPlayerIgnored as uiIsPlayerIgnored,
  killed as uiKilled,
  killedBy as uiKilledBy,
  noteSayEcho as uiNoteSayEcho,
  showSpectator as uiShowSpectator,
  updateGameInfo as uiUpdateGameInfo,
  updateHUD as uiUpdateHUD,
  updateLaserPointer as uiUpdateLaserPointer,
  visibilityHUD as uiVisibilityHUD,
  visibilityLaserPointer as uiVisibilityLaserPointer,
} from './UI';
import Vector from './Vector';
import type { PlayerUpdateMessage, ReelPlayer } from './types/Players';

const playersById = new Map<number, Player>();
const _impactPos = new Vector();

/** Syncs the plain player list to UIState for UI consumption. */
function syncUIPlayers(): void {
  const result: UIPlayer[] = [];
  for (const player of playersById.values()) {
    if (player.id === session.myID) continue;
    result.push({ id: player.id, name: player.name, bot: player.bot, team: player.team });
  }
  uiState.players = result;
}

/**
 * Tracks player IDs who currently have badges (top 3).
 * Used to detect when players drop out of top 3 and need badges removed.
 */
let previousTopPlayerIds: number[] = [];

/** Badge texture names indexed by rank: 0 = gold, 1 = silver, 2 = bronze */
const badgeTextureNames: readonly BadgeTextureName[] = ['badge_gold', 'badge_silver', 'badge_bronze'];

/** Updates all alive players and positions the camera. */
export function update() {
  for (const player of playersById.values()) {
    if (player.status === PLAYER_STATUS.ALIVE) {
      player.update(clock.timeFactor);
      player.updateGraphics();
    }
  }

  if (session.spectatingID != null) {
    const spectatedPlayer = playersById.get(session.spectatingID);
    if (spectatedPlayer == null) {
      return;
    }
    // Lag timeout: stop following if no packets for 3+ seconds
    if (networkTiming.timeNetwork - spectatedPlayer.lastPacket > PLAYER_TIMEOUT_MS) {
      return;
    }
    graphicsSetCamera(spectatedPlayer.pos.x, spectatedPlayer.pos.y);
  } else if (session.myID != null) {
    const myPlayer = playersById.get(session.myID);
    if (myPlayer == null) {
      return;
    }
    if (myPlayer.status === PLAYER_STATUS.ALIVE) {
      uiUpdateHUD(myPlayer.health, myPlayer.energy, myPlayer);
      uiUpdateLaserPointer(myPlayer.rot);
    }
    graphicsSetCamera(myPlayer.pos.x, myPlayer.pos.y);
  }
}

/** Adds a player and returns it. LOGIN lists its bots apart from its players, so that caller passes the flag. */
export function add(
  player: LoginPlayer | ServerPlayerNewMessage | ReelPlayer,
  isBot = false,
  fromLogin = false,
): Player {
  const newPlayer = new Player(player, isBot, fromLogin);
  playersById.set(player.id, newPlayer);

  // The reel's planes stay out of the UI mirror: nothing on the title screen
  // lists players, and each publish re-renders every mounted component.
  if (!newPlayer.reel) {
    syncUIPlayers();
  }

  if (session.state === GAME_STATE_CODES.PLAYING) {
    uiUpdateGameInfo();

    if (!fromLogin) {
      pushGameLogJoin(newPlayer);
    }
  }

  return newPlayer;
}

export function get(id: number): Player | undefined {
  return playersById.get(id);
}

export function getMe(): Player | undefined {
  if (session.myID == null) {
    return undefined;
  }
  return playersById.get(session.myID);
}

export function amIAlive() {
  return getMe()?.status === PLAYER_STATUS.ALIVE;
}

/** Iterates all tracked players. */
export function forEach(callback: (player: Player) => void) {
  for (const player of playersById.values()) {
    callback(player);
  }
}

export function getByName(name: string): Player | null {
  for (const player of playersById.values()) {
    if (player.name === name) return player;
  }
  return null;
}

/** A PLAYER_UPDATE, or the repel event for its owner and for each player it threw. */
export function networkUpdate(msg: PlayerUpdateMessage) {
  const player = playersById.get(msg.id);
  if (player != null) {
    player.networkUpdate(msg);
  }
}

export function fire(msg: ServerPlayerFireMessage) {
  const player = playersById.get(msg.id);
  if (player != null) {
    player.networkFire(msg);
  }
}

export function boost(msg: ServerEventBoostMessage) {
  const player = playersById.get(msg.id);
  if (player != null) {
    player.networkBoost(msg);
  }
}

export function bounce(msg: ServerEventBounceMessage) {
  const player = playersById.get(msg.id);
  if (player != null) {
    player.networkBounce(msg);
  }
}

/**
 * A nearby player's /s line: a speech bubble, and a chat line too, since the
 * server only sends CHAT_SAY for players in view, so it means someone next to
 * us said it, and the chat line persists.
 */
export function say(msg: ServerChatSayMessage) {
  const player = playersById.get(msg.id);
  if (player == null) {
    return;
  }
  // Our own echo is the delivery receipt for emotes and flag bubbles.
  uiNoteSayEcho(player.id);
  player.sayBubble(msg);
  uiAddSayLine(player, msg.text);
}

export function respawn(msg: ServerPlayerRespawnMessage) {
  const player = playersById.get(msg.id);
  if (player == null) {
    return;
  }
  player.respawn(msg);
  // If we're spectating this player and a death-triggered auto-switch is
  // pending, cancel it: they respawned, keep watching them.
  cancelSpectatorSwitchOnRespawn(msg.id);
  uiUpdateGameInfo();
}

/**
 * My flag across games: the saved setting or the backend's country at boot,
 * then each flag the server confirms. 'xx' until known. Importers read it
 * through this live binding; setMyFlag and requestMyFlag write it.
 */
export let myFlag: FlagCode = UNKNOWN_FLAG;
/** The flag the last /flag chose, applied once the server confirms the flag it was sent as. */
let requestedFlag: FlagCode = UNKNOWN_FLAG;

/** Sets my flag and the menu's copy of it. */
export function setMyFlag(flag: FlagCode): void {
  myFlag = flag;
  // The menu's Country Flag row and flag page show it while open
  uiState.myFlag = flag;
}

/** Records the flag a /flag command chose, to apply once the server confirms the flag it was sent as. */
export function requestMyFlag(flag: FlagCode): void {
  requestedFlag = flag;
}

export function changeFlag(msg: ServerPlayerFlagMessage) {
  const player = playersById.get(msg.id);
  if (player == null) {
    return;
  }
  if (msg.id === session.myID) {
    setMyFlag(requestedFlag);
    setSettings({
      flag: requestedFlag,
    });
  }
  player.changeFlag(msg);
}

export function stealth(msg: ServerEventStealthMessage) {
  const player = playersById.get(msg.id);
  if (player != null) {
    player.stealth(msg);
  }
}

export function leaveHorizon(msg: ServerEventLeaveHorizonMessage) {
  const player = playersById.get(msg.id);
  if (player != null) {
    player.leaveHorizon();
  }
}

/** Assigns gold/silver/bronze badges to the top 3 scoreboard players. */
export function updateBadges(scoreboardData: ScoreBoardData[]) {
  const topCount = toolsClamp(scoreboardData.length, 0, 3);
  const newTopPlayerIds: number[] = [];

  for (let rank = 0; rank < topCount; rank++) {
    const scoreEntry = scoreboardData[rank];
    if (scoreEntry == null) {
      continue;
    }

    const player = playersById.get(scoreEntry.id);
    if (player == null) {
      continue;
    }

    newTopPlayerIds.push(player.id);

    if (player.state.badge !== rank) {
      player.state.badge = rank;
      const badgeTexture = badgeTextureNames[rank];
      if (badgeTexture !== undefined) {
        player.changeBadge(badgeTexture);
      }
    }

    if (!player.state.hasBadge) {
      player.state.hasBadge = true;
      if (player.render && player.sprites.badge) {
        player.sprites.badge.visible = true;
      }
    }
  }

  // Remove badges from players no longer in top 3
  for (let i = 0; i < previousTopPlayerIds.length; i++) {
    const playerId = previousTopPlayerIds[i];
    if (playerId == null) {
      continue;
    }

    if (newTopPlayerIds.indexOf(playerId) === -1) {
      const player = playersById.get(playerId);
      if (player == null) {
        continue;
      }
      if (player.state.hasBadge) {
        player.state.hasBadge = false;
        if (player.sprites.badge) {
          player.sprites.badge.visible = false;
        }
      }
    }
  }

  previousTopPlayerIds = newTopPlayerIds;
}

export function chat(msg: ServerChatPublicMessage) {
  const player = playersById.get(msg.id);
  if (player != null) {
    // A -SWAM-PING is answered with a private whisper (client detection protocol)
    answerPing(player.id, msg.text);
    uiAddChatLine(player, msg.text, 'public');
  }
}

export function teamChat(msg: ServerChatTeamMessage) {
  const player = playersById.get(msg.id);
  if (player != null) {
    answerPing(player.id, msg.text);
    uiAddChatLine(player, msg.text, 'team');
  }
}

export function votemutePass(msg: ServerChatVoteMutePassedMessage) {
  const player = playersById.get(msg.id);
  if (player != null) {
    uiChatVotemutePass(player);
  }
}

export function whisper(msg: ServerChatWhisperMessage) {
  let otherPlayer: Player | undefined;
  let chatMode: ChatMessageType;
  // A -SWAM-PONG reads as a line about the sender's client
  const pong = pongDisplayText(msg.text);

  if (msg.to === session.myID) {
    // We received a whisper — show the sender
    otherPlayer = playersById.get(msg.from);
    if (otherPlayer == null) {
      return;
    }
    chatMode = 'whisper-from';

    // A -SWAM-PING whispered to us is answered like one in the chat
    answerPing(otherPlayer.id, msg.text);

    // Audible whisper notification — easy to miss otherwise. Skip for bots
    // (server messages, ping responses) and for ignored players.
    if (!otherPlayer.bot && !uiIsPlayerIgnored(otherPlayer.id) && pong === null) {
      soundUIClick();
    }
  } else {
    // We sent a whisper — show the recipient, except our own pong answers:
    // the server echoes them like any whisper, and StarMash keeps them out of the chat too
    if (pong !== null) {
      return;
    }
    otherPlayer = playersById.get(msg.to);
    if (otherPlayer == null) {
      return;
    }
    chatMode = 'whisper-to';
  }

  uiAddChatLine(otherPlayer, pong ?? msg.text, chatMode);
}

export function impact(msg: ServerPlayerHitMessage) {
  for (const hitData of msg.players) {
    const player = playersById.get(hitData.id);
    if (player != null) {
      _impactPos.set(msg.posX, msg.posY);
      player.impact(msg.type, _impactPos, hitData.health, hitData.healthRegen);
    }
  }
}

export function powerup(msg: ServerPlayerPowerupMessage) {
  const me = getMe();
  if (me != null) {
    me.powerup(msg);
  }
}

export function updateLevel(msg: ServerPlayerLevelMessage) {
  const player = playersById.get(msg.id);
  if (player != null) {
    player.updateLevel(msg);
  }
}

export function reteam(msg: PlayerReteamPlayer) {
  const player = playersById.get(msg.id);
  if (player != null) {
    player.reteam(msg.team);
  }

  // Keep session.myTeam in sync — server reshuffles teams between CTF matches,
  // and stale myTeam breaks every "is this player on my team?" check (nameplate
  // health bracket, friendly-fire UI cues, etc).
  if (msg.id === session.myID) {
    session.myTeam = msg.team;
  }

  syncUIPlayers();
  uiUpdateGameInfo();
}

/**
 * Handles player death: combat death (killer/pos non-zero) or
 * forced spectate (all zeros, silent entry into spectator mode).
 */
export function kill(msg: ServerPlayerKillMessage) {
  const player = playersById.get(msg.id);
  if (player == null) {
    return;
  }

  if (msg.killer != 0 || msg.posX != 0 || msg.posY != 0) {
    player.kill(msg);

    const killer = playersById.get(msg.killer);
    if (killer != null) {
      pushGameLogKill(killer, player);
    }

    if (player.me()) {
      uiVisibilityHUD(false);
      uiVisibilityLaserPointer(false);
      if (killer != null) {
        uiKilledBy(killer);
      }
      uiShowSpectator('enter');
    } else if (msg.killer === session.myID) {
      uiKilled(player);
    }

    // Auto-switch spectator target if watching this player die (non-BTR)
    if (!player.me() && player.id === session.spectatingID && session.gameType !== GAME_TYPES.BTR) {
      gamesSpectatorSwitch(player.id);
    }
  } else {
    // Forced spectate
    player.kill({
      posX: 0,
      posY: 0,
      spectate: true,
    });
    uiVisibilityHUD(false);
    uiVisibilityLaserPointer(false);
    uiUpdateGameInfo();
  }
}

/** Removes a player from the game entirely (disconnect/leave). */
export function destroy(id: number) {
  if (id === session.spectatingID) {
    gamesSpectatorSwitch(id);
  }

  const player = playersById.get(id);
  if (player != null) {
    if (session.state === GAME_STATE_CODES.PLAYING) {
      pushGameLogLeave(player);
    }

    player.destroy(true);
    playersById.delete(id);
    syncUIPlayers();

    if (session.state === GAME_STATE_CODES.PLAYING) {
      uiUpdateGameInfo();
    }
  }
}

export function changeType(msg: ServerPlayerTypeMessage) {
  const player = playersById.get(msg.id);
  if (player != null) {
    player.changeType(msg);
  }
}

/** Returns [active, total] player counts. Active = total - culled. */
export function count(): [number, number] {
  let totalCount = 0;
  let culledCount = 0;

  for (const player of playersById.values()) {
    totalCount++;
    if (player.culled) {
      culledCount++;
    }
  }

  const activeCount = totalCount - culledCount;
  return [activeCount, totalCount];
}

/** Counts players by category: human, bot, team, spectator. */
export function categoryCounts(): {
  players: number;
  bots: number;
  blueTeam: number;
  redTeam: number;
  spectators: number;
} {
  const counts = {
    players: 0,
    bots: 0,
    blueTeam: 0,
    redTeam: 0,
    spectators: 0,
  };

  for (const player of playersById.values()) {
    if (player.bot) {
      // Exclude the ab-server "Server" bot — it doesn't play
      if (player.name !== 'Server') {
        counts.bots++;
      }
    } else {
      counts.players++;

      if (player.isSpectating()) {
        counts.spectators++;
      } else {
        if (player.team === 1) {
          counts.blueTeam++;
        } else if (player.team === 2) {
          counts.redTeam++;
        }
      }
    }
  }

  return counts;
}

export function wipe() {
  for (const player of playersById.values()) {
    player.destroy(true);
  }
  playersById.clear();
  uiState.players = [];
  // Any minimap track ring was bound to a player id that no longer exists.
  // Clear so the UI state matches the wiped player set immediately rather than
  // waiting for the next minimap tick (which won't fire while disconnected).
  uiState.trackedPlayerId = null;
  // Open context menu was anchored to a player id that no longer exists; close
  // it so it doesn't render a stale name across the server switch.
  uiState.contextMenu = null;
}
